import { Label } from "components/label"
import { CollapsibleSection } from "../../../../../../components/collapsibleSection"
import Input from "components/input"
import { SwitchButton } from "components/switchButton"
import Select from "components/select"
import { Controller, UseFormReturn } from "react-hook-form"
import { EventType, IEditOrgForm } from "interfaces"
import { HttpError } from "@refinedev/core"
import { EVENT_TYPE_OPTIONS, NATIONALITY_OPTIONS, PHONE_CODE } from "utills/constants"
import { TextEditor } from "components/textEditor"
import CONFIG from "app-config/config.json";

export const OrganizationInformationSection = (props: UseFormReturn<IEditOrgForm, HttpError, IEditOrgForm>) => {
  const { register, watch, control, formState: { errors } } = props;
  return (
    <CollapsibleSection label="Organization Information">
      <div className="divide-y divide-gray-100">
        <div className="px-4 py-6 sm:px-6">
          <div className="grid gap-x-4 gap-y-6 sm:grid-cols-6 lg:gap-x-6">
            <div className="sm:col-span-6">
              <Label 
                required
                id="organization_name" 
                name="Organization name"  
                hint="The name will be used and displayed on the Organizer List page and the Organizer Details page on the ActiUp website."
              />
              <div className="grid gap-x-4 gap-y-6 sm:grid-cols-2 lg:gap-x-6">
                <div>
                  <p className="w-32 text-sm font-medium text-center max-w-full mb-[-4px] border border-magnolia-600 rounded-t-md bg-magnolia-500 px-4 py-2">
                    Vietnam
                  </p>
                  <Input 
                    id="organization_name_vi"
                    type="text"
                    placeholder="Organization name (vi)"
                    className="bg-white"
                    maxLength={100}
                    error={errors["name_vi"]}
                    {...register("name_vi")}
                  />
                  <div className="flex justify-between flex-row-reverse">
                    <p className="text-xs leading-6">{(watch("name_vi") ?? "").length}/100</p>
                    {errors["name_vi"] ? ( 
                      <p className="error-text">
                        <span className="font-medium">
                          {errors["name_vi"].message}
                        </span>
                      </p> )
                    : null}
                  </div>
                </div>
                <div>
                  <p className="w-32 text-sm font-medium text-center max-w-full mb-[-4px] border border-magnolia-600 rounded-t-md bg-magnolia-500 px-4 py-2">
                    English
                  </p>
                  <Input 
                    id="organization_name_en"
                    type="text"
                    placeholder="Organization name (en)"
                    className="bg-white"
                    maxLength={100}
                    error={errors["name_en"]}
                    {...register("name_en")}
                  />
                  <div className="flex justify-between flex-row-reverse">
                    <p className="text-xs leading-6">{(watch("name_en") ?? "").length}/100</p>
                    {errors["name_en"] ? ( 
                      <p className="error-text">
                        <span className="font-medium">
                          {errors["name_en"].message}
                        </span>
                      </p> )
                    : null}
                  </div>
                </div>
              </div>
            </div>
            <div className="sm:col-span-3">
              <div className="grid gap-x-4 gap-y-6 sm:grid-cols-2 lg:gap-x-6">
                <Controller
                  control={control}
                  name="is_active"
                  render={({ field: { onChange, value = true, ref } }) => (
                    <div>
                      <Label 
                        required
                        id="status" 
                        name="Status"  
                      />
                      <SwitchButton checked={value} onChange={onChange} />
                    </div>
                  )}
                />
                <Controller
                  control={control}
                  name="event_type"
                  render={({ field: { onChange, value = [], ref } }) => (
                    <div>
                      <Label 
                        required
                        id="event_type" 
                        name="Event type"  
                      />
                      <Select 
                        isMulti
                        options={EVENT_TYPE_OPTIONS} 
                        id="event_type"
                        value={EVENT_TYPE_OPTIONS.filter(item => value.includes(item.value))}
                        onChange={(selected: {label: string; value: EventType}[]) => onChange(selected.map(item => `${item.value}`).sort().reverse())}
                        error={errors["event_type"]}
                      />
                      {errors["event_type"] ? ( 
                        <p className="error-text">
                          <span className="font-medium">
                            {errors["event_type"].message}
                          </span>
                        </p> )
                      : null}
                    </div>
                  )}
                />
              </div>
            </div>
            <div className="sm:col-span-3">
              <Label 
                required
                id="country" 
                name="Country"  
              />
              <Controller
                control={control}
                name="country"
                render={({ field: { onChange, value = "", ref } }) => (
                  <Select 
                    inputRef={ref}
                    options={NATIONALITY_OPTIONS} 
                    id="country"
                    placeholder="Select Country"
                    value={NATIONALITY_OPTIONS.find(c => c.label === value)}
                    onChange={(selected: {label: string; value: string;}) => onChange(selected?.label)}
                    error={errors["country"]}
                  />
                )}
              />
              {errors["country"] ? ( 
                <p className="error-text">
                  <span className="font-medium">
                    {errors["country"]?.message}
                  </span>
                </p> )
              : null}
            </div>
            <div className="sm:col-span-3">
              <Label 
                required
                id="workspace_domain" 
                name="Workspace domain"
                hint="Creating a subdomain for accessing the dedicated workspace tool for the organization."
              />
              <div className="flex">
                <Input
                  disabled
                  id="workspace_domain"
                  className="rounded-r-none"
                  placeholder="Subdomain name"
                  maxLength={50}
                  error={errors["workspace_domain"]}
                  {...register("workspace_domain", {
                    disabled: true
                  })}
                />
                <span className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-100 px-3 text-gray-500 sm:text-sm">
                  .workspace.actiup.net
                </span>
              </div>
            </div>
            <div className="sm:col-span-3">
              <Label 
                required
                id="landing_page" 
                name="Landing page domain"
                hint="Creating a subdomain for accessing the dedicated landing page for the organization."
              />
              <div className="flex">
                <Input
                  disabled
                  id="landing_page"
                  className="rounded-r-none"
                  placeholder="Subdomain name"
                  maxLength={50}
                  error={errors["landing_page"]}
                  {...register("landing_page", {
                    disabled: true
                  })}
                />
                <span className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-100 px-3 text-gray-500 sm:text-sm">
                  .actiup.net
                </span>
              </div>
            </div>
            <div className="sm:col-span-3">
              <Label 
                required
                id="organization_slug" 
                name="Organization slug"
                hint="It is used to create a 'address bar' for the Organization Detail page on the actiup.net website. Format: text-text-text"
              />
              <div className="flex">
                <p className="shrink-0 flex max-w-[50%] items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-100 px-3 text-gray-500 sm:text-sm" style={{wordBreak: "break-word"}}>
                  <span className="line-clamp-2 w-full">{CONFIG.DOMAIN}/organizers/</span>
                </p>
                <Input
                  id="organization_slug"
                  className="rounded-l-none"
                  placeholder="Slug name"
                  maxLength={100}
                  error={errors["organization_slug"]}
                  {...register("organization_slug")}
                />
              </div>
              <div className="flex justify-between flex-row-reverse">
                <p className="text-xs leading-6">{(watch("organization_slug") ?? "").length}/100</p>
                {errors["organization_slug"] ? ( 
                  <p className="error-text">
                    <span className="font-medium">
                      {errors["organization_slug"]?.message}
                    </span>
                  </p> )
                : null}
              </div>
            </div>
            <div className="sm:col-span-3">
              <Label 
                id="homepage_position" 
                name="Homepage position"
                hint="Used to set the display order on the Homepage of the actiup.net website. Setting from 1 to 6"
              />
              <Input
                id="homepage_position"
                type="number"
                placeholder="Position"
                min={0}
                max={6}
                error={errors["homepage_position"]}
                {...register("homepage_position")}
              />
              {errors["homepage_position"] ? ( 
                <p className="error-text">
                  <span className="font-medium">
                    {errors["homepage_position"]?.message}
                  </span>
                </p> )
              : null}
            </div>
            <div className="sm:col-span-6">
              <Label 
                required
                id="organization_introduction" 
                name="Organization introduction"  
                hint="The introduction content will be displayed in the “About” section when accessing the Organizer Detail page on the actiup.net website."
              />
              <div className="grid gap-x-4 gap-y-6 sm:grid-cols-2 lg:gap-x-6">
                <div>
                  <p className="w-32 text-sm font-medium text-center max-w-full mb-[-4px] border border-magnolia-600 rounded-t-md bg-magnolia-500 px-4 py-2">
                    Vietnam
                  </p>
                  <Controller
                    control={control}
                    name="introduction_vi"
                    render={({ field: { onChange, value = "" } }) => (
                      <TextEditor
                        value={value ?? ""}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
                <div>
                  <p className="w-32 text-sm font-medium text-center max-w-full mb-[-4px] border border-magnolia-600 rounded-t-md bg-magnolia-500 px-4 py-2">
                    English
                  </p>
                  <Controller
                    control={control}
                    name="introduction_en"
                    render={({ field: { onChange, value = "" } }) => (
                      <TextEditor
                        value={value ?? ""}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
              </div>
            </div>

            <div className="sm:col-span-3">
              <Label 
                id="contact_phone" 
                name="Contact phone"
              />
              <div className="flex">
                <Controller
                  control={control}
                  name="contact_phone_area_code"
                  render={({ field: { onChange, value = "", ref } }) => {
                    const areaPhoneCodeArr = PHONE_CODE.map(item => ({label: item.label, value: item.code}));
                    return (
                      <Select 
                        ref={ref}
                        className="flex-1 w-[160px] !rounded-none inline-flex items-center !rounded-l-md"
                        options={areaPhoneCodeArr} 
                        id="contact_phone_area_code"
                        value={areaPhoneCodeArr.find(c => c.value === value)}
                        onChange={(selected: {label: string; value: string;}) => onChange(selected?.value)}
                        error={errors["contact_phone_area_code"]}
                      />
                    )
                  }}
                />
                <Input
                  id="contact_phone"
                  type="phone"
                  className={`${errors["contact_phone"] ? "border-l-[1px] focus:!ml-0" : "border-l-0"} rounded-l-none focus:border-l-[1px] focus:ml-[-1px] z-[2]`}
                  placeholder="Phone number"
                  error={errors["contact_phone"]}
                  {...register("contact_phone")}
                />
              </div>
              {errors["contact_phone"] ? ( 
                <p className="error-text">
                  <span className="font-medium">
                    {errors["contact_phone"]?.message}
                  </span>
                </p> )
              : null}
            </div>
            <div className="sm:col-span-3">
              <Label 
                id="contact_email" 
                name="Contact email"  
              />
              <Input 
                id="contact_email"
                type="text"
                placeholder="Contact Email"
                error={errors["contact_email"]}
                {...register("contact_email")}  
              />
              {errors["contact_email"] ? ( 
                <p className="error-text">
                  <span className="font-medium">
                    {errors["contact_email"]?.message}
                  </span>
                </p> )
              : null}
            </div>
          </div>
        </div>
      </div>
    </CollapsibleSection>
  )
}